'use client';

import { useEffect, useRef, useState } from 'react';

/**
 * How good is the connection between this browser and the translator?
 *
 * TWO measurements, because a connection can fail in two unrelated ways and
 * showing only one of them mislabels half the people who are suffering:
 *
 *   SPEED — how fast bytes actually arrive, in Mbps, timed against a block of
 *   random data from our own server. This is what people mean by "network
 *   speed", and it is what separates a visitor on rural mobile from one on
 *   fibre.
 *
 *   DELAY — the round trip to a 204 with no body. A translation is only a few
 *   hundred bytes, so on a healthy line the delay is most of the wait, and it
 *   is paid again on every streamed chunk of the answer. A cafe wifi that
 *   answers instantly and then trickles, and a satellite link that delivers
 *   megabits a second and a half late, are both bad here — and each one looks
 *   perfect through the other's number.
 *
 * The verdict on screen is the WORSE of the two, so neither failure can hide
 * behind the other.
 *
 * Both are measured against OUR server rather than a CDN or a speed-test host.
 * The question is "is the connection to the translator healthy?", and only the
 * real path can answer it — the wifi can be perfect while the route to this box
 * is not.
 */

const API = process.env.NEXT_PUBLIC_API_BASE ?? '';

export type NetQuality = 'fast' | 'good' | 'slow' | 'poor' | 'offline' | 'unknown';

export interface NetState {
  /** Round-trip milliseconds, or null before the first measurement lands. */
  rtt: number | null;
  /**
   * Measured download speed in Mbps — the headline number.
   *
   * Null until the first block has been timed, and null for the whole session
   * when the visitor has Data Saver on: someone who has asked their browser to
   * spend less traffic must not have 128 KB spent on telling them how fast it
   * would have arrived.
   */
  mbps: number | null;
  /** The worse of the speed verdict and the delay verdict. */
  quality: NetQuality;
  /** Which of the two is dragging the verdict down — for the tooltip's advice. */
  limiter: 'speed' | 'delay' | null;
  /**
   * The browser's own estimate, where it has one.
   *
   * Chromium-only (`navigator.connection`), absent in Safari and Firefox, and
   * coarse where present. Kept only as a fallback for the seconds before our
   * own measurement lands — a figure that vanishes on half of browsers cannot
   * be what the chip is built around.
   */
  downlinkMbps: number | null;
  /** A measurement is in flight. Drives nothing but the pulse on the icon. */
  probing: boolean;
}

/**
 * Where the bands sit.
 *
 * Chosen against the real path rather than against a textbook: this server is
 * in Finland and much of the traffic is from Korea, which is ~250-330 ms of
 * pure distance on a healthy connection. Bands that called 300 ms "slow" would
 * show amber to every Korean visitor on a perfect line, and a warning that is
 * always on is furniture.
 *
 * So `good` is generous and `slow` starts where the delay becomes something a
 * person can feel on top of the model's own thinking time.
 */
const FAST = 150;
const GOOD = 400;
const SLOW = 900;

export function classifyDelay(rtt: number | null): NetQuality {
  if (rtt === null) return 'unknown';
  if (rtt <= FAST) return 'fast';
  if (rtt <= GOOD) return 'good';
  if (rtt <= SLOW) return 'slow';
  return 'poor';
}

/**
 * And where the SPEED bands sit.
 *
 * Anchored to what this page actually needs rather than to what a speed test
 * would call impressive. A translation is a few hundred bytes and the page
 * itself is under a megabyte, so 10 Mbps and 200 Mbps feel identical here —
 * both are "nothing is waiting on bandwidth". The bands are therefore set where
 * the experience genuinely changes: below ~1.5 Mbps the page itself becomes a
 * wait, and below ~0.4 Mbps (2G, a crowded conference wifi) even a streamed
 * sentence arrives in visible jerks.
 */
const MBPS_FAST = 10;
const MBPS_GOOD = 1.5;
const MBPS_SLOW = 0.4;

export function classifySpeed(mbps: number | null): NetQuality {
  if (mbps === null) return 'unknown';
  if (mbps >= MBPS_FAST) return 'fast';
  if (mbps >= MBPS_GOOD) return 'good';
  if (mbps >= MBPS_SLOW) return 'slow';
  return 'poor';
}

/** Worst-wins, so neither half of a bad connection can hide behind the other. */
const RANK: NetQuality[] = ['unknown', 'fast', 'good', 'slow', 'poor', 'offline'];
export function worse(a: NetQuality, b: NetQuality): NetQuality {
  if (a === 'unknown') return b;
  if (b === 'unknown') return a;
  return RANK.indexOf(a) >= RANK.indexOf(b) ? a : b;
}

/** How often to re-measure the delay while somebody is looking at the page. */
const INTERVAL_MS = 25_000;
/**
 * The speed probe runs far less often than the delay probe, and the asymmetry
 * is the point: a ping is a few bytes and a speed test is 128 KB. Re-measuring
 * that every 25 seconds would spend ~18 MB an hour of somebody's mobile data to
 * keep a chip up to date, which is a worse thing to do to a slow connection
 * than saying nothing about it.
 *
 * Ten minutes is only the backstop. What actually triggers a fresh reading is
 * evidence that the connection CHANGED: coming back online, the browser
 * reporting a different network, or the delay jumping into another band (see
 * `lastDelayBand`). A page left open on an unchanging wifi therefore measures
 * once and then costs nothing, while a phone that walks out of range re-reads
 * within one 25-second tick.
 */
const SPEED_INTERVAL_MS = 10 * 60_000;
/** A probe that has not answered in this long is not a slow line, it is a wall. */
const TIMEOUT_MS = 8_000;
/** The same, for the block download — generous, because a slow line is the point. */
const SPEED_TIMEOUT_MS = 15_000;

/** Small enough not to hurt a slow connection; see the adaptive step below. */
const FIRST_BLOCK = 128 * 1024;
const BIG_BLOCK = 512 * 1024;

function downlink(): number | null {
  const c = (navigator as any)?.connection;
  const d = c?.downlink;
  return typeof d === 'number' && d > 0 ? d : null;
}

/** The visitor has asked their browser to spend less data. Honour it. */
function saveData(): boolean {
  return (navigator as any)?.connection?.saveData === true;
}

/**
 * Time a block of bytes coming down the wire.
 *
 * Two details decide whether the number means anything:
 *
 *   • the clock stops when the BODY has been read, not when the headers land.
 *     `await fetch()` resolves on the response head, which on a slow line is
 *     most of a second before the last byte — timing that would report the
 *     speed of a connection nobody has yet used. Hence `arrayBuffer()`.
 *
 *   • the first block is deliberately small (128 KB), and only a connection
 *     that swallows it in under 200 ms is asked for a bigger one. On fibre,
 *     128 KB is over before TCP has finished working out how fast it is allowed
 *     to go, so the measurement reads low and jumps around; on 2G, half a
 *     megabyte is ten seconds of somebody's day and their data allowance. The
 *     adaptive step gives the accurate answer to the fast connection and the
 *     kind one to the slow connection, which is the right way round.
 */
async function measureSpeed(signal: AbortSignal, allowBigBlock: boolean): Promise<number | null> {
  const once = async (bytes: number): Promise<number | null> => {
    const url = `${API}/api/ping/data?bytes=${bytes}&t=${Date.now()}`;
    const started = performance.now();
    try {
      const res = await fetch(url, { cache: 'no-store', signal });
      if (!res.ok) return null;
      const buf = await res.arrayBuffer();
      const seconds = (performance.now() - started) / 1000;
      if (seconds <= 0 || buf.byteLength === 0) return null;
      return (buf.byteLength * 8) / seconds / 1_000_000;
    } catch {
      return null;
    }
  };

  const started = performance.now();
  const first = await once(FIRST_BLOCK);
  if (first === null) return null;

  // The bigger block only on the FIRST reading of a session, where the accuracy
  // is worth the bytes. Later re-reads are checking whether something changed,
  // and 128 KB answers that just as well for a quarter of the traffic.
  if (allowBigBlock && performance.now() - started < 200) {
    const second = await once(BIG_BLOCK);
    // The bigger sample is the better one when it exists, for the reason above:
    // it is the one measured after TCP reached full speed.
    if (second !== null) return Math.round(second * 10) / 10;
  }
  return Math.round(first * 10) / 10;
}

/**
 * One measurement: two probes, and the FASTER of the two is the answer.
 *
 * Not an average, and not a single request. The first call after an idle
 * moment can carry a TCP and TLS handshake that the second one reuses, so a
 * single sample regularly reads 2-3x the true round trip and the light would
 * blink amber every 25 seconds on a healthy connection. Averaging keeps half of
 * that error. The minimum of two is the standard way out: latency has a floor
 * and no ceiling, so the smallest sample is always the honest one — every
 * millisecond above it is something that happened to that packet, not a
 * property of the connection.
 */
async function probe(signal: AbortSignal): Promise<number | null> {
  let best: number | null = null;

  for (let i = 0; i < 2; i++) {
    // A fresh query string every time. A 204 should not be cacheable and says
    // so, but a proxy or an over-eager browser that served one from memory
    // would report ~0 ms for a connection that is not there at all — the one
    // wrong answer this must never give.
    const url = `${API}/api/ping?t=${Date.now()}-${i}`;
    const started = performance.now();
    try {
      const res = await fetch(url, { cache: 'no-store', signal });
      if (!res.ok && res.status !== 204) return null;
      const ms = performance.now() - started;
      best = best === null ? ms : Math.min(best, ms);
    } catch {
      // Aborted (unmount, or a new probe overtaking this one) or genuinely
      // unreachable. Either way this sample says nothing.
      return null;
    }
  }

  return best === null ? null : Math.round(best);
}

export function useNetworkSpeed(): NetState {
  const [state, setState] = useState<NetState>({
    rtt: null,
    mbps: null,
    quality: 'unknown',
    limiter: null,
    downlinkMbps: null,
    probing: false,
  });

  const inflight = useRef<AbortController | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** When the speed was last measured, so it can run on its own slower clock. */
  const speedAt = useRef(0);
  /** The last speed reading, held between the delay probes that overtake it. */
  const lastMbps = useRef<number | null>(null);
  /** Has the accurate first reading been taken? Only it gets the 512 KB block. */
  const measuredOnce = useRef(false);
  /**
   * The delay band at the last reading.
   *
   * The cheap probe doubles as the trigger for the expensive one: a ping that
   * moves from `fast` to `slow` is the connection changing under somebody, and
   * that is exactly when a three-minute-old speed reading is a lie. Watching
   * the band rather than the raw number keeps ordinary jitter — 40ms becoming
   * 90ms — from spending 128 KB to confirm nothing happened.
   */
  const lastDelayBand = useRef<NetQuality>('unknown');

  useEffect(() => {
    let alive = true;

    const run = async () => {
      // Offline is not worth a request: the browser already knows, and a fetch
      // against a dead interface takes the full timeout to tell us the same
      // thing. `onLine` is only trustworthy in the negative direction — true
      // means "there is an interface", not "the internet works" — which is
      // exactly how it is used here.
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        lastMbps.current = null;
        if (alive) {
          setState({
            rtt: null, mbps: null, quality: 'offline', limiter: null,
            downlinkMbps: null, probing: false,
          });
        }
        return;
      }

      inflight.current?.abort();
      const controller = new AbortController();
      inflight.current = controller;
      const cutoff = setTimeout(() => controller.abort(), TIMEOUT_MS);

      if (alive) setState((s) => ({ ...s, probing: true }));
      const rtt = await probe(controller.signal);
      clearTimeout(cutoff);
      if (!alive || controller.signal.aborted) return;

      // A probe that failed or timed out is not "unknown" — the visitor pressed
      // nothing and learned nothing, but the connection refused to answer, and
      // that is the worst reading there is.
      const delayQ: NetQuality = rtt === null
        ? (navigator.onLine === false ? 'offline' : 'poor')
        : classifyDelay(rtt);

      // The delay first, because it is cheap and answers in milliseconds; the
      // speed only when its own slower clock is due, or when the delay has just
      // said the connection is not the one we measured. So the chip is never
      // waiting on a 128 KB download to say something true, and an unchanging
      // wifi is never asked for those bytes twice.
      let mbps = lastMbps.current;
      const changed = delayQ !== lastDelayBand.current && lastDelayBand.current !== 'unknown';
      const speedDue = changed || Date.now() - speedAt.current > SPEED_INTERVAL_MS;
      lastDelayBand.current = delayQ;

      if (rtt !== null && speedDue && !saveData()) {
        const big = setTimeout(() => controller.abort(), SPEED_TIMEOUT_MS);
        const measured = await measureSpeed(controller.signal, !measuredOnce.current);
        clearTimeout(big);
        if (!alive) return;
        if (measured !== null) {
          mbps = measured;
          lastMbps.current = measured;
          speedAt.current = Date.now();
          measuredOnce.current = true;
        }
      }
      const speedQ = classifySpeed(mbps);
      const quality = worse(delayQ, speedQ);

      setState({
        rtt,
        mbps,
        quality,
        // Which half to blame, for the tooltip. Only worth naming when the two
        // disagree — when both are bad, "your connection is bad" is the whole
        // story and picking a culprit would be false precision.
        limiter:
          quality === 'offline' || quality === 'unknown' || delayQ === speedQ
            ? null
            : quality === speedQ ? 'speed' : 'delay',
        downlinkMbps: downlink(),
        probing: false,
      });
    };

    /** Measure now, then keep a slow heartbeat going while the tab is visible. */
    const schedule = () => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(async () => {
        if (document.visibilityState === 'visible') await run();
        schedule();
      }, INTERVAL_MS);
    };

    void run();
    schedule();

    // The three moments where the reading on screen is certainly stale: the
    // connection dropped, it came back, or the tab has been in the background
    // for who knows how long and is being looked at again.
    const onOffline = () => {
      lastMbps.current = null;
      setState({ rtt: null, mbps: null, quality: 'offline', limiter: null, downlinkMbps: null, probing: false });
    };
    // Coming back from an outage, or switching from wifi to mobile, is a
    // different connection — not the same one a minute later. Both reset the
    // speed clock so the next probe re-measures instead of showing what the
    // previous network could do.
    const onOnline = () => { speedAt.current = 0; void run(); };
    const onVisible = () => { if (document.visibilityState === 'visible') void run(); };
    const conn = (navigator as any)?.connection;
    const onConnChange = () => {
      speedAt.current = 0;
      setState((s) => ({ ...s, downlinkMbps: downlink() }));
      void run();
    };

    window.addEventListener('offline', onOffline);
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisible);
    conn?.addEventListener?.('change', onConnChange);

    return () => {
      alive = false;
      inflight.current?.abort();
      if (timer.current) clearTimeout(timer.current);
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisible);
      conn?.removeEventListener?.('change', onConnChange);
    };
  }, []);

  return state;
}
